package br.com.texeratx.boardgametimeapp.ui.screens.matches.detail

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.EmojiEvents
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDateTime
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MatchDetailUI(
    match: MatchListItemUI,
    state: MatchDetailUiState,
    onBack: () -> Unit,
    onEdit: () -> Unit,
    onDeleteClick: () -> Unit = {},
    showDeleteConfirmation: Boolean = false,
    isDeleting: Boolean = false,
    onDismissDeleteConfirmation: () -> Unit = {},
    onConfirmDelete: () -> Unit = {}
) {
    val S = LocalStrings.current

    if (showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = onDismissDeleteConfirmation,
            title = { Text(S.deleteMatch) },
            text = { Text(S.deleteMatchConfirm) },
            confirmButton = {
                TextButton(
                    onClick = onConfirmDelete,
                    enabled = !isDeleting
                ) {
                    Text(S.deleteMatch)
                }
            },
            dismissButton = {
                TextButton(onClick = onDismissDeleteConfirmation) {
                    Text(S.cancel)
                }
            }
        )
    }

    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(S.matchDetailTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                actions = {
                    IconButton(
                        onClick = onEdit
                    ) {
                        Icon(
                            imageVector = Icons.Default.Edit,
                            contentDescription = S.editMatch
                        )
                    }
                }
            )
        },
        bottomBar = {
            DeleteMatchBottomBar(onDeleteClick = onDeleteClick)
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            item { MatchHeader(match) }

            if (match.expansionIds.isNotEmpty()) {
                item {
                    ExpansionsSection(state)
                }
            }

            item { PlayersSection(match) }
        }
    }
}

@Composable
private fun MatchHeader(match: MatchListItemUI) {
    Card(shape = RoundedCornerShape(16.dp)) {
        Box(modifier = Modifier.padding(16.dp)) {

            // CONTEÚDO PRINCIPAL
            Row(
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {

                // IMAGEM DO JOGO
                GameThumb(
                    imageUrl = match.game.imageUrl,
                    contentDescription = match.game.name,
                    modifier = Modifier
                        .size(110.dp)
                        .clip(RoundedCornerShape(12.dp))
                )

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .padding(end = 8.dp)
                ) {

                    Text(
                        text = match.game.name,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )

                    // 📅 DATA FORMATADA
                    Text(
                        text = apiToUiDateTime(match.date),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            // ⏱ DURAÇÃO — CANTO INFERIOR DIREITO
            Box(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .background(
                        MaterialTheme.colorScheme.primaryContainer,
                        RoundedCornerShape(12.dp)
                    )
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        Icons.Default.Schedule,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(Modifier.width(6.dp))
                    Text(
                        "${match.duration} min",
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }
    }
}

@Composable
private fun ExpansionsSection(state: MatchDetailUiState) {
    val S = LocalStrings.current

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {

        Text(
            S.sectionExpansions,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold
        )

        when {
            state.isLoading -> {
                Text(
                    text = S.loadingExpansions,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            state.error != null -> {
                Text(
                    text = state.error,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error
                )
            }

            state.expansions.isEmpty() -> {
                Text(
                    text = S.noExpansionsFound,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            else -> {
                state.expansions.forEach { exp ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {

                        GameThumb(
                            imageUrl = exp.imageUrl,
                            contentDescription = exp.name,
                            modifier = Modifier
                                .size(48.dp)
                                .clip(RoundedCornerShape(8.dp))
                        )

                        Text(exp.name)
                    }
                }
            }
        }
    }
}

@Composable
private fun PlayersSection(match: MatchListItemUI) {
    val S = LocalStrings.current

    val orderedPlayers = match.players.sortedByDescending { it.points }
    val winnerId = orderedPlayers.firstOrNull()?.id

    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {

        Text(
            S.sectionPlayers,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold
        )

        Card(
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {

                orderedPlayers.forEachIndexed { index, player ->

                    val isWinner = player.id == winnerId

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {

                        // 🏅 POSIÇÃO
                        Text(
                            text = S.positionFormat(index + 1),
                            fontWeight = FontWeight.Bold,
                            color = if (isWinner)
                                MaterialTheme.colorScheme.primary
                            else
                                MaterialTheme.colorScheme.onSurfaceVariant
                        )

                        // 👤 AVATAR MAIOR
                        if (!player.imageUrl.isNullOrBlank()) {
                            PlayerAvatar(
                                imageUrl = player.imageUrl,
                                contentDescription = player.name,
                                modifier = Modifier
                                    .size(36.dp)
                                    .clip(CircleShape)
                            )
                        } else {
                            Box(
                                modifier = Modifier
                                    .size(36.dp)
                                    .clip(CircleShape)
                                    .background(MaterialTheme.colorScheme.primaryContainer),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = (player.nickname ?: player.name)
                                        .first()
                                        .uppercase(),
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }

                        // NOME + SCORE
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = player.nickname ?: player.name,
                                fontWeight = if (isWinner) FontWeight.Bold else FontWeight.Medium
                            )
                            Text(
                                text = S.pointsFormat(player.points.toInt()),
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }

                        // 🏆 ÍCONE VENCEDOR
                        if (isWinner) {
                            Icon(
                                imageVector = Icons.Default.EmojiEvents,
                                contentDescription = S.winner,
                                tint = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DeleteMatchBottomBar(onDeleteClick: () -> Unit) {
    val S = LocalStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surface)
            .navigationBarsPadding()
            .padding(16.dp)
    ) {
        androidx.compose.material3.Button(
            onClick = onDeleteClick,
            modifier = Modifier.fillMaxWidth(),
            colors = androidx.compose.material3.ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.error
            )
        ) {
            Text(
                text = S.deleteMatch,
                color = MaterialTheme.colorScheme.onError,
                fontWeight = FontWeight.Bold
            )
        }
    }
}



